import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Xmark, ArrowLeft, CircleCheck, Circle, Flame, ChevronRight } from "@gravity-ui/icons";
import { useGameContext } from "../../contexts/GameContext";
import axios from "axios";
import { getCookie } from "../../utils/cookieUtils";
import AchievementCard from "./AchievementModal";
import { useAchievement } from "../../contexts/AchievementContext";
import { usePostHog } from "posthog-js/react";

const CheckBoxIcon = "/CheckMark.svg";
const EmptyCheckMarkIcon = "/EmptyCheckMark.svg";
const StreakIcon = "/StreakIcon.svg";
const LeadboardIcon = "/LeadboardIcon.svg";
const StarIcon = "/StarIcon.svg";
const MilestoneIcon1 = "/UpdateMinutes.svg";
const MilestoneIcon2 = "/UpdateMinutes.svg";
const MilestoneIcon3 = "/DiscountOffer.svg";
const LeftArrowIcon = "/LeftArrowIcon.svg";

const weekIcons = {
    Sunday: {
        inactive: "/week-icons/SundayStreakInactive.svg",
        active: "/week-icons/SundayStreakActive.svg"
    },
    Monday: {
        inactive: "/week-icons/MondayStreakInactive.svg",
        active: "/week-icons/MondayStreakActive.svg"
    },
    Tuesday: {
        inactive: "/week-icons/TuesdayStreakInactive.svg",
        active: "/week-icons/TuesdayStreakActive.svg"
    },
    Wednesday: {
        inactive: "/week-icons/WednesdayStreakInactive.svg",
        active: "/week-icons/WednesdayStreakActive.svg"
    },
    Thursday: {
        inactive: "/week-icons/ThursdayStreakInactive.svg",
        active: "/week-icons/ThursdayStreakActive.svg"
    },
    Friday: {
        inactive: "/week-icons/FridayStreakInactive.svg",
        active: "/week-icons/FridayStreakActive.svg"
    },
    Saturday: {
        inactive: "/week-icons/SaturdayStreakInactive.svg",
        active: "/week-icons/SaturdayStreakActive.svg"
    }
};

const getRankSuffix = (rank) => {
    // Special case for 11, 12, 13 which use "th" despite ending in 1, 2, or 3
    if (rank % 100 >= 11 && rank % 100 <= 13) {
        return "th";
    }
    
    // Check the last digit
    switch (rank % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
    }
};
const GameModal = ({ onClose }) => {
    // Get checkAndTriggerMilestone from context
    const { gamificationData, checkAndTriggerMilestone } = useGameContext();
    const posthog = usePostHog();

    const [isVisible, setIsVisible] = useState(false);
    const [milestoneSection, setMilestoneSection] = useState(true);
    const [streakSection, setStreakSection] = useState(false);
    const [exploreMore, setExploreMore] = useState(false);
    const [completedDays, setCompletedDays] = useState([]);

    const completedTasksCount = gamificationData?.tasks?.filter(task => task.is_completed).length || 0;
    const totalTasks = gamificationData?.tasks?.length || 0;
    const progress = totalTasks > 0 ? Math.floor((completedTasksCount / totalTasks) * 100) : 0;

    useEffect(() => {
        if (gamificationData && gamificationData.completedDays) {
            setCompletedDays(gamificationData.completedDays);
        } else {
            // Default to empty array if completedDays doesn't exist in gamificationData
            setCompletedDays([]);
        }
    }, [gamificationData]);

    const milestones = [
        { id: 1, position: 2 },
        { id: 2, name: "5 mins", position: 33, img: MilestoneIcon1 },
        { id: 3, name: "10 mins", position: 66, img: MilestoneIcon2 },
        { id: 4, name: "15 mins", position: 98, img: MilestoneIcon2 },
    ];    // Reference to the modal element
    const modalRef = useRef(null);

    useEffect(() => {
        setIsVisible(true);
        
        // Handler for ESC key press
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };
        
        // Add event listener for ESC key
        document.addEventListener('keydown', handleEscKey);
        
        // Cleanup function
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, []);
    
    // Handler for outside clicks
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };
        
        // Add event listener for clicks
        document.addEventListener('mousedown', handleOutsideClick);
        
        // Cleanup function
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    // Add effect to check milestones when tasks are completed
    useEffect(() => {
        if (progress > 0 && gamificationData?.tasks) {
            checkAndTriggerMilestone(progress);
        }
    }, [gamificationData?.tasks, checkAndTriggerMilestone]);

    const handleClose = () => {
        posthog.capture("Click-Close-GameModal", {
            is_user_logged_in: true
        });
        setIsVisible(false);
        setTimeout(onClose, 300);
    }
    return (
        <>
            <AnimatePresence>
                {isVisible && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="fixed inset-y-0 md:bottom-14 bottom-[136px] md:right-2 right-0 z-50 flex items-end justify-end p-4"
                    >                        <motion.div
                            ref={modalRef}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="bg-[#1D1B1D] text-white rounded-xl shadow-lg md:w-[400px] overflow-auto relative border border-white/10 flex flex-col"
                        >
                            {/* Close Button */}
                            <button
                                className="absolute top-3 right-3 flex w-8 h-8 items-center justify-center text-white/50 hover:text-white bg-white/5 hover:bg-white/10 rounded-full transition duration-200"
                                onClick={handleClose}
                            >
                                <Xmark className="w-4 h-4" />
                            </button>

                            {/* Header */}
                            {!exploreMore ? (
                                <div className="flex-grow p-3">
                                    <h2 className="font-extralight text-sm mb-1">Hi {gamificationData?.full_name},</h2>
                                    <p className="text-white text-base w-[90%]">You're the {gamificationData.rank}{getRankSuffix(gamificationData.rank)} most active user in the world!</p>
                                </div>
                            ) : (
                                <div className="flex items-center p-3 gap-1.5 border-b border-white/10">
                                    <button
                                        className="w-8 h-8 flex items-center justify-center text-white/40 hover:bg-white/5 rounded-full hover:text-white transition duration-200"
                                        onClick={() => {
                                            setExploreMore(false)
                                            posthog.capture("Click-GoBack-GameModal", {
                                                is_user_logged_in: true
                                            })}
                                        }
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                    <h2 className="text-base font-normal">All tasks</h2>
                                </div>
                            )}

                            {/* Content based on mode */}
                            <AnimatePresence mode="wait">
                                {exploreMore ? (
                                    <motion.div
                                        key="exploreMore"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className=""
                                    >
                                        <div className="p-3">
                                            <div className="grid grid-cols-3 gap-2">
                                                {gamificationData.tasks.map((task, index) => (
                                                    <div
                                                        key={index}
                                                        className={` h-20 md:w-full py-2 px-2 rounded-lg flex flex-col items-center justify-center ${task.is_completed ? 'border border-white/5 text-white/30' : 'border border-transparent bg-white/5 text-white'
                                                            }`}
                                                    >
                                                        {task.is_completed ? (
                                                            <CircleCheck className="w-5 h-5 mb-1" />
                                                        ) : (
                                                            <Circle className="w-5 h-5 mb-1 flex-shrink-0" />
                                                        )}
                                                        <span className="text-center font-extralight text-xs leading-0">{task.label}</span>

                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <>
                                        {/* Stats Section */}
                                        {/* Stats Section */}
                                        <div className="grid grid-cols-3 gap-2 p-3 pt-0">
                                            {/* Goals Completed */}
                                            <div
                                                className={`flex flex-col items-center justify-center text-center px-2 h-20 rounded-lg border cursor-pointer 
                                        ${milestoneSection ? "bg-[#BA18EC1A] border-purple-500" : "border-white/5"}`}
                                                onClick={() => {
                                                    setMilestoneSection(true);
                                                    setStreakSection(false);
                                                    posthog.capture("Click-TaskCompletionTab-GameModal", {
                                                        is_user_logged_in: true
                                                    })
                                                }}
                                            >
                                                <div className="flex flex-col items-center justify-center w-full">
                                                    <div className="flex justify-center w-full mb-1">
                                                        <CircleCheck className="w-5 h-5" />
                                                    </div>
                                                    <div className="text-base font-normal">{completedTasksCount}</div>
                                                    <div className="text-white/60 md:text-xs text-[10px]">goals completed</div>
                                                </div>
                                            </div>

                                            {/* Days Creating */}
                                            <div
                                                className={`flex flex-col items-center justify-center text-center px-2 h-20 rounded-lg border cursor-pointer 
                                        ${streakSection ? "bg-[#BA18EC1A] border-purple-500" : "border-white/5"}`}
                                                onClick={() => {
                                                    posthog.capture("Click-StreakValueTab-GameModal", {
                                                        is_user_logged_in: true
                                                    })
                                                    setMilestoneSection(false);
                                                    setStreakSection(true);
                                                }}
                                            >
                                                <div className="flex flex-col items-center justify-center w-full">
                                                    <div className="flex justify-center w-full mb-1">
                                                        <Flame className="w-5 h-5" />
                                                    </div>
                                                    <div className="text-base font-normal">{gamificationData?.streak}</div>
                                                    <div className="text-white/60 md:text-xs text-[10px]">days creating</div>
                                                </div>
                                            </div>

                                            {/* Most Active User */}
                                            <div className="flex flex-col items-center justify-center text-center px-2 h-20 rounded-lg border border-white/5">
                                                <div className="flex flex-col items-center justify-center w-full">
                                                    <div className="flex justify-center w-full mb-1">
                                                        <img src={LeadboardIcon} alt="Leaderboard" className="w-5 h-5" />
                                                    </div>
                                                    <div className="text-base font-normal">{gamificationData.rank}{getRankSuffix(gamificationData.rank)}
                                                    </div>
                                                    <div className="text-white/60 md:text-xs text-[10px]">most active user</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Section Animation */}
                                        <AnimatePresence mode="wait">
                                            {milestoneSection && (
                                                <motion.div
                                                    key="milestoneSection"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                >
                                                    <div className="mx-3 mb-3 p-3 rounded-lg border border-white/5">
                                                        {/* Earn Milestone Section */}
                                                        <h3 className="text-base font-normal flex items-center gap-2">
                                                            <img src={StarIcon} alt="Star Icon" className="w-4 h-4" />
                                                            Earn milestone
                                                        </h3>

                                                        {/* Progress Bar with Dots */}

                                                        <div className="relative w-full mt-2 mb-6">
                                                            <div className="w-full h-3 bg-white/5 rounded-full">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition-all duration-500 ease-in-out"
                                                                    style={{ width: `${progress}%` }}
                                                                />
                                                            </div>

                                                            <div className="absolute top-0 left-0 w-full h-full">
                                                                {milestones.map((milestone) => (
                                                                    <div
                                                                        key={milestone.id}
                                                                        className={`absolute w-1.5 h-1.5 rounded-full transition-colors duration-500 ${milestone.position <= progress ? "bg-white" : "bg-[#686768]"}`}
                                                                        style={{
                                                                            left: `${milestone.position}%`,
                                                                            transform: "translate(-50%, -50%)",
                                                                            top: "50%",
                                                                        }}
                                                                    >
                                                                        {milestone.id !== 1 && (
                                                                            <div
                                                                                className={`absolute flex flex-col top-full mt-2 ml-1 text-xs text-white/50 transform ${milestone.id === 4
                                                                                    ? "-translate-x-9 items-end ml-1 md:ml-1 "
                                                                                    : "-translate-x-1/2 text-center"
                                                                                    }`}
                                                                                style={{ whiteSpace: "nowrap" }}
                                                                            >
                                                                                <img
                                                                                    src={milestone.img}
                                                                                    alt={milestone.name}
                                                                                    className={`w-4 h-4 mb-1 mt-1  ${milestone.id === 4 ? "md:ml-auto ml-6" : "mx-auto"}`}
                                                                                />
                                                                                {milestone.name}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Next Up Section */}
                                                        <div className="mt-12">
                                                            <h3 className="text-sm font-normal mb-2">Next Up</h3>
                                                            <div className="grid grid-cols-3 gap-2 ">
                                                                {/* Sign Up Button */}
                                                                <button className="border h-20 text-xs md:text-xs w-full border-white/5 text-white/40 px-2 rounded-lg flex flex-col items-center justify-center cursor-not-allowed">
                                                                    <CircleCheck className="w-5 h-5 mb-1" />
                                                                    Sign up!
                                                                </button>

                                                                {/* Generate Track Button */}
                                                                <button className="border h-20 text-xs md:text-xs w-full border-white/5 text-white/40 px-2 rounded-lg flex flex-col items-center justify-center">
                                                                    {gamificationData.tasks[1].is_completed === true ? <CircleCheck className="w-5 h-5 mb-1" /> : <Circle className="w-5 h-5 mb-1" />}
                                                                    Generate a track
                                                                </button>

                                                                {/* Explore More Button */}
                                                                <button
                                                                    className="bg-white/5 hover:bg-white/10 gap-1 h-20 text-xs md:text-xs w-full text-white/40 hover:text-white px-2 rounded-lg flex items-center justify-center cursor-pointer"
                                                                    onClick={() => {
                                                                        setExploreMore(true)
                                                                        posthog.capture("Click-ExploreMore-GameModal", {
                                                                            is_user_logged_in: true
                                                                        })
                                                                    }}
                                                                >
                                                                    Explore more
                                                                    <ChevronRight className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}

                                            {streakSection && (
                                                <motion.div
                                                    key="streakSection"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                                    className="px-3 w-full"
                                                >
                                                    <div className="p-3 mb-3 rounded-lg border border-white/5 w-full text-white">
                                                        <h3 className="text-base sm:text-base font-normal flex items-center gap-2">
                                                            <Flame className="w-5 h-5" />
                                                            Weekly streaks
                                                        </h3>

                                                        <p className="text-white/60 text-sm font-extralight">
                                                            Create a track everyday for the next {7 - completedDays.length} days to get 3 bonus minutes of music free
                                                        </p>

                                                        {/* Progress Days & Bonus Button */}
                                                        <div className="flex w-full items-center mt-3 gap-3 ">
                                                            <div className="flex gap-1 py-1 w-full">
                                                                {Object.entries(weekIcons).map(([day, icons]) => (
                                                                    <div
                                                                        key={day}
                                                                        className="flex flex-col items-center min-w-[2rem]"
                                                                    >

                                                                        <img
                                                                            src={
                                                                                completedDays.includes(day)
                                                                                    ? icons.active
                                                                                    : icons.inactive
                                                                            } alt={day}
                                                                            className="w-8 h-8"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            <div className="border border-orange-500 text-white px-3 py-1 rounded-full text-xs transition whitespace-nowrap">
                                                                +3 mins
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default GameModal;